/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Global } from '@emotion/react'
import type { GridReadyEvent } from 'ag-grid-community'
import { useEffect, useRef } from 'react'
import colors from '@sportninja/common/constants/appColors'
import {
  isTeamRosterFull,
  teamIdentity,
} from '@sportninja/common/features/registration/startStep/teamDisplay'
import type {
  DivisionTeam,
  StartRole,
} from '@sportninja/common/features/registration/startStep/types'
import type { Division } from '@sportninja/common/types'
import DataGrid from 'src/components/ts-components/DataGrid'
import {
  buildDivisionColumns,
  buildTeamColumns,
} from '../utils/entityGridColumns'
import type { DivisionRow, TeamRow } from '../utils/entityGridColumns'

/**
 * Tall enough to show several rows without the grid becoming the whole page.
 * Fixed rather than `autoHeight`: a competition can carry hundreds of teams, and
 * materialising every row throws away the virtualization that keeps scrolling
 * responsive.
 */
const GRID_HEIGHT = 420
const ROW_HEIGHT = 44
const HEADER_HEIGHT = 44

interface Props {
  tab: 'divisions' | 'teams'
  /** Decides which half of the fee pair the division columns show. */
  role: StartRole | null
  currencyName: string

  divisions: Division[]
  selectedDivisionId?: string | null
  onSelectDivision: (division: Division) => void

  teamResults: DivisionTeam<Division>[]
  selectedTeamId?: string | null
  onSelectTeam: (entry: DivisionTeam<Division>) => void
}

/**
 * The list view of the two entity tabs.
 *
 * Selecting a row IS selecting a card — it calls the same handlers the grids of
 * cards do, so the division-first and team-first behaviours built around them
 * carry over untouched rather than being re-implemented for a second surface.
 *
 * One component for both tabs because they differ only in their rows and their
 * columns; the selection wiring, sizing and empty state are identical, and two
 * files would mean maintaining that agreement by hand.
 */
const EntityGrid = ({
  tab,
  role,
  currencyName,
  divisions,
  selectedDivisionId,
  onSelectDivision,
  teamResults,
  selectedTeamId,
  onSelectTeam,
}: Props) => {
  const isDivisions = tab === 'divisions'
  const apiRef = useRef<GridReadyEvent['api'] | null>(null)

  const selectedId = isDivisions ? selectedDivisionId : selectedTeamId

  /*
   * Mirror the current selection into the grid.
   *
   * The answer lives in the step's own state, not in AG Grid: a reader can pick
   * on the cards, switch to the list, and must see their choice already
   * selected. Driven by id rather than by the row object, because the row
   * arrays are rebuilt on every search keystroke.
   */
  const applySelection = (
    api: GridReadyEvent['api'] | null,
    id: string | null | undefined
  ) => {
    if (api == null) {
      return
    }
    api.forEachNode((node) => {
      const shouldSelect = node.id != null && node.id === id
      if (node.isSelected() !== shouldSelect) {
        node.setSelected(shouldSelect)
      }
    })
  }

  useEffect(() => {
    applySelection(apiRef.current, selectedId)
  }, [selectedId, divisions, teamResults])

  /*
   * Applied again as each grid becomes ready.
   *
   * Switching tabs MOUNTS a new grid (see the per-tab key below), whose nodes
   * start unselected. The effect above cannot cover that: none of its
   * dependencies changes on a tab switch, and even with `tab` added it would
   * run against the destroyed grid's API — `onGridReady` is the one callback
   * that fires after the replacement exists.
   */
  const handleGridReady = (event: GridReadyEvent) => {
    apiRef.current = event.api
    applySelection(event.api, selectedId)
  }

  /*
   * Single row: this is a chooser, not a bulk-edit sheet.
   *
   * `enableClickSelection` is deliberately OFF. The click is handled by
   * `onRowClicked` and answered by the step, whose handlers toggle; letting AG
   * Grid also select on the same click makes two writers of one piece of state,
   * and they disagree precisely on the re-click that clears a selection. The
   * grid's own highlight is driven from the step instead, by the effect above.
   */
  const ROW_SELECTION = {
    mode: 'singleRow' as const,
    enableClickSelection: false,
    checkboxes: false,
    headerCheckbox: false,
  }

  return (
    <div css={wrapperStyle}>
      {/*
        GLOBAL, not nested in `wrapperStyle`.

        AG Grid renders the tooltip into a portal outside this component's DOM
        subtree, so a descendant rule here would never reach it. Left
        unconstrained it stretches to the length of a description, which for a
        paragraph is a line running most of the window.
      */}
      <Global styles={tooltipStyles} />

      {isDivisions ? (
        <DataGrid<DivisionRow>
          /*
           * A distinct key per tab, so switching tabs MOUNTS a new grid rather
           * than re-configuring the live one.
           *
           * Both branches sit at the same position in the tree, so React treats
           * them as one component and merely swaps `rowData`, `columnDefs` and
           * `getRowId`. AG Grid then holds row nodes created under the other
           * tab's `getRowId` against the new columns — the nodes survive, their
           * data does not match, and every value getter reads `undefined` until
           * a horizontal scroll forces the cells to be rebuilt.
           */
          key='divisions'
          rowData={divisions}
          columnDefs={buildDivisionColumns(role, currencyName)}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          rowSelection={ROW_SELECTION}
          tooltipShowDelay={500}
          onGridReady={handleGridReady}
          getRowId={({ data }) => data.id}
          /*
           * Driven by the CLICK, not by AG Grid's selection change.
           *
           * `singleRow` does not deselect when the already-selected row is
           * clicked again, so a selection-based handler never hears about it —
           * and the step's own `handleSelectDivision` is what implements the
           * toggle, exactly as the cards use it. Forwarding every click lets a
           * second click on the same row clear it; the effect above then
           * reconciles the grid's own selection from the step's state.
           */
          onRowClicked={(event) => {
            if (event.data != null) {
              onSelectDivision(event.data)
            }
          }}
          noRowsOverlayComponentParams={{
            message: 'No Divisions Match Your Filters',
          }}
        />
      ) : (
        <DataGrid<TeamRow>
          //  See the divisions grid above: a per-tab key is what keeps the two
          //  from sharing one AG Grid instance and its row nodes.
          key='teams'
          rowData={teamResults}
          columnDefs={buildTeamColumns()}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          rowSelection={ROW_SELECTION}
          tooltipShowDelay={500}
          onGridReady={handleGridReady}
          //  The team's own id, matching what `selectedTeamId` carries — the
          //  division is not unique across rows here.
          getRowId={({ data }) => teamIdentity(data.team) ?? ''}
          //  Click-driven for the same reason as the divisions grid above: the
          //  step owns the toggle, and a re-click on the selected row is the
          //  one event a selection handler would never see.
          //  A full roster cannot take another registration, so the row is inert
          //  — the same rule the card enforces by disabling itself. The roster
          //  column already states the count; `rowClass` greys the row so it
          //  reads as unavailable rather than merely unresponsive.
          onRowClicked={(event) => {
            if (event.data != null && !isTeamRosterFull(event.data.team)) {
              onSelectTeam(event.data)
            }
          }}
          getRowClass={({ data }) =>
            data && isTeamRosterFull(data.team) ? 'sn-row-disabled' : undefined
          }
          noRowsOverlayComponentParams={{
            message: 'No Teams Or Drafts Match Your Search',
          }}
        />
      )}
    </div>
  )
}

export default EntityGrid

/* The shared DataGrid's own wrapper is width-only — every other grid in the app
   uses autoHeight. Fixed-height virtualization needs a height here, or the AG
   Grid root collapses and the rows render into a box nobody can see. */
/* Caps the description tooltip to a readable measure and lets it wrap. Without
   a max-width AG Grid sizes the bubble to the text on one line, so a paragraph
   becomes a rule running most of the window. */
const tooltipStyles = css`
  .ag-tooltip {
    max-width: 280px;
    white-space: normal;
    overflow-wrap: break-word;
    line-height: 1.45;
  }
`

const wrapperStyle = css`
  width: 100%;
  min-width: 0;
  height: ${GRID_HEIGHT}px;

  > div,
  .ag-root-wrapper {
    height: 100%;
  }

  /* A whole row is one target here, so it should look like one. */
  .ag-row {
    cursor: pointer;
  }

  /*
   * No divider after the LAST header cell.
   *
   * The theme already turns off headerColumnBorder, so these lines are not
   * that: they are the resize handles Quartz draws on every header cell, which
   * no theme param governs. On the last cell the line has nothing to its right
   * to separate, so it reads as an edge on the table rather than a divider.
   *
   * It costs that column its drag-to-resize target — acceptable here because
   * the last column is the flex one, which takes whatever width is left over
   * rather than holding a size a drag could set.
   */
  .ag-header-row .ag-header-cell:last-of-type .ag-header-cell-resize {
    display: none;
  }

  /*
   * Keyed to AG Grid's OWN selected-row class, not a class of ours.
   *
   * A getRowClass result is only recomputed when a row is created or
   * refreshed — never merely because the selection changed — so a hand-rolled
   * class left the previously picked row still striped after the next pick.
   * The ag-row-selected class is maintained by the grid itself, so it is always
   * current.
   */
  .ag-row-selected .ag-cell {
    background-color: ${colors.NEUTRAL_700};
  }

  .ag-row-selected .ag-cell:first-of-type {
    box-shadow: inset 3px 0 0 ${colors.PRIMARY};
  }

  /*
   * A full team cannot take another registration, so its row reads as
   * unavailable and the click handler ignores it.
   *
   * Safe to drive from getRowClass despite the caveat above: unlike selection,
   * how full a team is does not change while the grid is mounted, so the class
   * never needs recomputing between renders.
   */
  .ag-row.sn-row-disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  /*
   * The roster count keeps full strength on a dimmed row.
   *
   * It is the REASON the row is unavailable, so dimming it along with
   * everything else would mute the one cell that explains the state. Restored
   * to full opacity rather than lightening the row's, so the rest of the row
   * still recedes.
   */
  .ag-row.sn-row-disabled .ag-cell[col-id='roster'] {
    opacity: 1;
  }
`
